import { useEffect, useState } from "react";
import { useLanguage } from "../../contexts/LanguageContext";
import { fetchAPI, formatSportLabel, selectableSports } from "../../lib/utils";
import { Input } from "../ui/input";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "../ui/select";

export const SPORT_NONE = "__none__";
export const SPORT_OTHER = "__other__";

/**
 * La scelta del campo, ricavata dalla gara.
 *
 * `sport` valorizzato: la gara e' collegata a quello sport. `sport === ""`:
 * l'utente ha scelto «Altro» e scrive lui. Senza sport ma con un testo: una
 * gara scritta a mano prima del collegamento, o di uno sport non censito, e
 * anche questa e' «Altro». Niente di tutto questo: nessuno sport.
 */
export function sportChoiceOf(form) {
	if (form?.sport) return form.sport;
	if (form?.sport === "" || String(form?.discipline || "").trim()) return SPORT_OTHER;
	return SPORT_NONE;
}

/**
 * Il form dopo una scelta. Uno sport porta con se' il suo nome in
 * `discipline`, cosi' ogni vista che legge il testo continua a mostrarlo;
 * «Altro» scollega (`sport: ""`, che il server legge come «nessun
 * collegamento») e tiene il testo libero, tranne il nome dello sport appena
 * lasciato; «nessuno» svuota entrambi.
 */
export function applySportChoice(form, choice, options) {
	if (choice === SPORT_NONE) return { ...form, sport: null, discipline: "" };
	if (choice === SPORT_OTHER) {
		const linkedLabel = form?.sport ? formatSportLabel(form.sport, options) : null;
		const text = String(form?.discipline || "");
		return { ...form, sport: "", discipline: text && text !== linkedLabel ? text : "" };
	}
	return { ...form, sport: choice, discipline: formatSportLabel(choice, options) };
}

/**
 * Lo sport di una gara (D16 di docs/sport-config): uno degli sport della
 * piattaforma, oppure «Altro» col testo libero per le gare di sport non
 * censiti, oppure nessuno.
 *
 * Collegare la gara a uno sport la fa entrare nei filtri per sport dei
 * calendari, e in futuro nell'analisi agonistica; il testo libero resta per
 * le gare «strane», che non si devono perdere. E' **uno** per i due form
 * delle gare (calendario gare del coach e scheda atleta).
 *
 * Gli sport sono quelli che chi scrive vede (`/sports`): si propongono quelli
 * selezionabili, piu' quello gia' collegato, anche se non piu' in elenco.
 *
 * Props: form, setForm (come uno `useState`), triggerClassName.
 */
export function CompetitionSportField({ form, setForm, triggerClassName = "" }) {
	const { t } = useLanguage();
	const [options, setOptions] = useState([]);

	useEffect(() => {
		let cancelled = false;
		fetchAPI("/sports")
			.then((data) => {
				if (!cancelled) setOptions(Array.isArray(data) ? data : []);
			})
			.catch(() => {
				if (!cancelled) setOptions([]);
			});
		return () => {
			cancelled = true;
		};
	}, []);

	const choice = sportChoiceOf(form);
	const visible = selectableSports(options, form?.sport ? [form.sport] : []);
	const linkedMissing =
		form?.sport && !visible.some((option) => option.value === form.sport);

	return (
		<div className="space-y-2">
			<Select
				value={choice}
				onValueChange={(value) =>
					setForm((current) => applySportChoice(current, value, options))
				}
			>
				<SelectTrigger
					className={`bg-background border-border rounded-sm ${triggerClassName}`}
					aria-label={t("competitionSport")}
				>
					<SelectValue />
				</SelectTrigger>
				<SelectContent className="bg-card border-border">
					<SelectItem value={SPORT_NONE}>{t("competitionSportNone")}</SelectItem>
					{visible.map((option) => (
						<SelectItem key={option.value} value={option.value}>
							{option.label || formatSportLabel(option.value, options)}
						</SelectItem>
					))}
					{linkedMissing && (
						<SelectItem value={form.sport}>
							{form.discipline || formatSportLabel(form.sport, options)}
						</SelectItem>
					)}
					<SelectItem value={SPORT_OTHER}>{t("competitionSportOther")}</SelectItem>
				</SelectContent>
			</Select>
			{choice === SPORT_OTHER && (
				<>
					<Input
						className="bg-background border-border rounded-sm"
						value={form?.discipline || ""}
						onChange={(event) => {
							// Il valore si legge subito: dentro l'aggiornamento differito
							// l'input controllato e' gia' tornato al valore di prima.
							const text = event.target.value;
							setForm((current) => ({ ...current, sport: "", discipline: text }));
						}}
						aria-label={t("competitionDiscipline")}
						placeholder={t("competitionDisciplinePlaceholder")}
					/>
					<p className="text-[11px] text-muted-foreground">
						{t("competitionSportOtherHint")}
					</p>
				</>
			)}
		</div>
	);
}
